import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Logo } from "@/components/Logo";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import loginBg from "@/assets/login-bg.jpg";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Loader2, Eye, EyeOff, Check, X } from "lucide-react";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";

const acceptInvitationSchema = z.object({
  password: z.string()
    .min(8, "Password must be at least 8 characters")
    .max(128, "Password is too long")
    .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
    .regex(/[a-z]/, "Password must contain at least one lowercase letter")
    .regex(/[0-9]/, "Password must contain at least one number"),
  confirmPassword: z.string()
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

interface InvitationData {
  email: string;
  full_name: string | null;
  team_id: string;
  invited_by: string;
  status: string;
  expires_at: string;
}

const AcceptInvitation = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<z.infer<typeof acceptInvitationSchema>>({
    resolver: zodResolver(acceptInvitationSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const password = form.watch("password");

  // Password requirement checks
  const passwordRequirements = [
    { label: "At least 8 characters", met: password.length >= 8 },
    { label: "One uppercase letter", met: /[A-Z]/.test(password) },
    { label: "One lowercase letter", met: /[a-z]/.test(password) },
    { label: "One number", met: /[0-9]/.test(password) },
  ];

  useEffect(() => {
    const fetchLogo = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('north_logo_url')
        .not('north_logo_url', 'is', null)
        .limit(1)
        .maybeSingle();
      
      if (data?.north_logo_url) {
        setLogoUrl(data.north_logo_url);
      }
    };
    fetchLogo();
  }, []);

  useEffect(() => {
    const validateInvitation = async () => {
      const token = searchParams.get('token');

      if (!token) {
        toast({
          title: "Invalid Invitation",
          description: "No invitation token provided",
          variant: "destructive",
        });
        navigate('/login');
        return;
      }

      try {
        const { data, error } = await supabase
          .from('team_invitations')
          .select('email, full_name, team_id, invited_by, status, expires_at')
          .eq('invitation_token', token)
          .maybeSingle();

        if (error || !data) {
          throw new Error("Invitation not found");
        }

        if (data.status !== 'pending') {
          throw new Error("This invitation has already been used");
        }

        if (new Date(data.expires_at) < new Date()) {
          throw new Error("This invitation has expired");
        }

        setInvitation(data);
      } catch (error: any) {
        toast({
          title: "Invalid Invitation",
          description: error.message || "This invitation is not valid",
          variant: "destructive",
        });
        navigate('/login');
      } finally {
        setValidating(false);
      }
    };

    validateInvitation();
  }, [searchParams, navigate, toast]);

  const handleAccept = async (values: z.infer<typeof acceptInvitationSchema>) => {
    if (!invitation) return;

    setIsLoading(true);

    try {
      // Check captcha token
      if (!captchaToken) {
        toast({
          title: "Verification required",
          description: "Please complete the security check",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      const token = searchParams.get('token');

      // Create user account
      const { error: signupError } = await supabase.auth.signUp({
        email: invitation.email,
        password: values.password,
        options: {
          captchaToken,
          data: {
            full_name: invitation.full_name || invitation.email,
            team_id: invitation.team_id, // Pass team_id to auth hook
            invited_by: invitation.invited_by,
            invitation_token: token, // Pass token to mark invitation as accepted
          },
          emailRedirectTo: `${window.location.origin}/dashboard`,
        },
      });

      if (signupError) {
        // Reset turnstile on error
        turnstileRef.current?.reset();
        setCaptchaToken(null);

        // Handle captcha verification errors specifically
        if (signupError.message?.toLowerCase().includes('captcha') || 
            signupError.message?.toLowerCase().includes('verification')) {
          toast({
            title: "Verification Failed",
            description: "The security check failed. Please complete the CAPTCHA again and try once more.",
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }

        throw signupError;
      }

      toast({
        title: "Account created successfully!",
        description: "Welcome to the team. Redirecting to dashboard...",
      });

      // Redirect to dashboard with invite param so redirect check doesn't race with DB writes
      setTimeout(() => {
        navigate('/dashboard?invite=accepted');
      }, 1000);
    } catch (error: any) {
      // Reset turnstile on error
      turnstileRef.current?.reset();
      setCaptchaToken(null);

      // Provide user-friendly error messages
      let errorMessage = error.message || "Failed to create account";
      
      // Handle captcha-related errors
      if (errorMessage.toLowerCase().includes('captcha') || 
          errorMessage.toLowerCase().includes('verification')) {
        errorMessage = "Security verification failed. Please complete the CAPTCHA again and try again.";
      }

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen relative flex items-center justify-center p-4">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${loginBg})` }}
        />
        <div className="absolute inset-0 bg-black/60" />
        <Card className="w-full max-w-md relative z-10">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Validating invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!invitation) {
    return null;
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${loginBg})` }}
      />
      <div className="absolute inset-0 bg-black/60" />
      
      <Card className="w-full max-w-md relative z-10">
        <CardHeader className="space-y-6">
          <div className="flex justify-center pt-2">
            <Logo customLogoUrl={logoUrl} size="lg" />
          </div>
          <CardTitle className="text-2xl text-center">Accept Team Invitation</CardTitle>
          <CardDescription className="text-center">
            You've been invited to join a team. Create your password to get started.
          </CardDescription>
          <div className="text-sm text-center text-muted-foreground">
            <p>Email: <strong>{invitation.email}</strong></p>
            {invitation.full_name && <p>Name: <strong>{invitation.full_name}</strong></p>}
          </div>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleAccept)} className="space-y-4">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          {...field}
                          disabled={isLoading}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={isLoading}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Password Requirements */}
              {password && (
                <div className="space-y-2 p-3 bg-muted/50 rounded-md">
                  <p className="text-sm font-medium">Password Requirements:</p>
                  {passwordRequirements.map((req, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      {req.met ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <X className="h-4 w-4 text-red-500" />
                      )}
                      <span className={req.met ? "text-green-600" : "text-red-500"}>
                        {req.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm Password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="••••••••"
                          {...field}
                          disabled={isLoading}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          disabled={isLoading}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Turnstile CAPTCHA */}
              {import.meta.env.VITE_TURNSTILE_SITE_KEY ? (
                <div className="flex justify-center">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                    onSuccess={setCaptchaToken}
                    onError={() => {
                      setCaptchaToken(null);
                      toast({
                        title: "Verification Error",
                        description: "CAPTCHA verification failed. Please try again.",
                        variant: "destructive",
                      });
                    }}
                    onExpire={() => {
                      setCaptchaToken(null);
                      toast({
                        title: "Verification Expired",
                        description: "The security check expired. Please complete it again.",
                        variant: "destructive",
                      });
                    }}
                    options={{
                      theme: "light",
                      size: "normal",
                    }}
                  />
                </div>
              ) : (
                <div className="text-center text-sm text-muted-foreground p-4 bg-muted/50 rounded-md">
                  <p>Security verification is not configured. Please contact support.</p>
                </div>
              )}

              <Button type="submit" variant="gold" className="w-full" disabled={isLoading || !captchaToken}>
                {isLoading ? "Creating account..." : "Accept Invitation & Create Account"}
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AcceptInvitation;